import React, { useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { Form, OverlayTrigger } from 'react-bootstrap';
import { BsFilter, BsList, BsSearch, BsTable } from "react-icons/bs";
import { DataViewCategoryType, LS_COMPANY_INFO_KEY } from '../../../core/constants/common';
import Utils from '../../../core/utilities/Utils';
import './QualitativeFilterStatus.scss';

const MONTH_LABEL_KEYS = {
  JANUARY: 'January.label', FEBRUARY: 'February.label', MARCH: 'March.label', APRIL: 'April.label',
  MAY: 'May.label', JUNE: 'June.label', JULY: 'July.label', AUGUST: 'August.label',
  SEPTEMBER: 'September.label', OCTOBER: 'October.label', NOVEMBER: 'November.label', DECEMBER: 'December.label',
};
const MONTH_NUMS = {
  JANUARY: 1, FEBRUARY: 2, MARCH: 3, APRIL: 4, MAY: 5, JUNE: 6, JULY: 7, AUGUST: 8,
  SEPTEMBER: 9, OCTOBER: 10, NOVEMBER: 11, DECEMBER: 12,
};

const QualitativeFilterStatus = ({
	t,
	filter,
	topics,
	onFilterClick,
	showViewToggle = true,
	onTableViewNavigate,
	onListViewNavigate,
	initialViewMode = 'collapse',
}) => {
  const [viewMode, setViewMode] = useState(initialViewMode);
  const getCategoryName = (categoryType) => {
    const numericCategoryType = Number(categoryType);
    
    switch (numericCategoryType) {
      case DataViewCategoryType.ENVIRONMENTAL:
        return t("Environment.label");
      case DataViewCategoryType.SOCIAL:
        return t("Social.label");
      case DataViewCategoryType.GOVERNANCE:
        return t("Governance.label");
      default:
        return t("Environment.label");
    }
  };

  const getYearFromPeriod = (reportingPeriod) => {
    if (!reportingPeriod) return new Date().getFullYear();
    return new Date(reportingPeriod).getFullYear();
  };

  const getSelectedTopics = () => {
    if (!filter.topicIds || filter.topicIds.length === 0) return [];
    
    return topics?.filter(topic => filter.topicIds.includes(topic.id)) || [];
  };

  const formatTopics = (selectedTopics) => {
    if (selectedTopics.length === 0) return '';
    
    if (selectedTopics.length === 1) {
      return selectedTopics[0].name;
    } else if (selectedTopics.length <= 3) {
      return selectedTopics.map(topic => topic.name).join(', ');
    } else {
      return `${selectedTopics[0].name} and ${selectedTopics.length - 1} more`;
    }
  };

  const selectedTopics = getSelectedTopics();
  const formattedTopics = formatTopics(selectedTopics);

  const displayYear = getYearFromPeriod(filter.reportingPeriod);
  let companyInfo = {};
  try {
    companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
  } catch {
    companyInfo = {};
  }
  const dataFreqType = companyInfo?.frequencyType ?? companyInfo?.dataFrequencyType;
  const isFinancialYear = dataFreqType === 'FY';

  const periodRangeText = useMemo(() => {
    const y = Number(displayYear);
    const safeY = Number.isFinite(y) ? y : new Date().getFullYear();
    if (isFinancialYear) {
      const startMonth = companyInfo?.financialYearStart || 'APRIL';
      const endMonth = companyInfo?.financialYearEnd || 'MARCH';
      const startLabel = MONTH_LABEL_KEYS[startMonth] ? t(MONTH_LABEL_KEYS[startMonth]) : t('April.label');
      const endLabel = MONTH_LABEL_KEYS[endMonth] ? t(MONTH_LABEL_KEYS[endMonth]) : t('March.label');
      const sm = MONTH_NUMS[startMonth] ?? 4;
      const em = MONTH_NUMS[endMonth] ?? 3;
      const startY = sm > em ? safeY - 1 : safeY;
      return `${startLabel} ${startY} ${t('HomePage.periodTo.label')} ${endLabel} ${safeY}`;
    }
    return `${t('January.label')} ${safeY} ${t('HomePage.periodTo.label')} ${t('December.label')} ${safeY}`;
  }, [t, displayYear, isFinancialYear, companyInfo?.financialYearStart, companyInfo?.financialYearEnd]);

  return (
    <div className="qualitative-filter-status">
      <div className="qualitative-period-range" data-testid="qualitative-period-range">
        <span className="qualitative-period-range__label">{t('Period.label')}:</span>
        <span className="qualitative-period-range__value">{periodRangeText}</span>
      </div>

      {/* Category Filter - Always show */}
      <span className="Filter_status">
        <h6>{t("Category.label")}: </h6>
        {getCategoryName(filter.categoryType)}
      </span>

      {/* Topics Filter - Show if topics are selected */}
      {formattedTopics && (
        <span className="Filter_status">
          <h6>{t("Topic.label")}: </h6>
          {formattedTopics}
        </span>
      )}

      <div className="qualitative-filter-actions d-flex align-items-center gap-2 ms-auto flex-shrink-0">
        <OverlayTrigger
          placement="top"
          overlay={Utils.renderTooltip(
            t("Filter.label")
          )}>
          <button
            type="button"
            className="btn btn-headbtn filter-icon"
            onClick={onFilterClick}
          >
            <BsFilter />
          </button>
        </OverlayTrigger>

        {showViewToggle && (
          <div className="qualitative-view-controls" data-testid="qualitative-view-toggle">
            <div className="view-toggle-buttons d-flex align-items-center">
              <Form.Check
                inline
                label={<BsList aria-hidden />}
                type="radio"
                id="qualitativeViewCollapse"
                name="qualitativeViewMode"
                value="collapse"
                checked={viewMode === 'collapse'}
                onChange={() => {
                  setViewMode('collapse');
                  onListViewNavigate?.();
                }}
                aria-label={t('MaterialTopic.viewList.aria.label')}
              />
              <Form.Check
                inline
                label={<BsSearch aria-hidden />}
                type="radio"
                id="qualitativeViewTable"
                name="qualitativeViewMode"
                value="table"
                checked={viewMode === 'table'}
                onChange={() => {
                  setViewMode('table');
                  onTableViewNavigate?.();
                }}
                aria-label={t('MaterialTopic.viewTable.aria.label')}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default withTranslation()(QualitativeFilterStatus);
